import type { TrackPoints } from '@/lib/db';

/**
 * Track points as bytes, for the `.fdv` archive's `points/<trackId>.bin`.
 *
 * Version 1 archives carried points as JSON number arrays inside `db.json`.
 * That was fine for a backup taken by hand once a month and wrong for a sync
 * file rewritten every few minutes: a `Float32Array` value like 102.3 comes
 * back out of `Array.from` as `102.30000305175781`, so the JSON was several
 * times the size of the numbers and slow to parse on a phone. The columns
 * are already typed arrays; writing their bytes is exact and needs no
 * parsing at all.
 *
 * Layout, little-endian throughout, `n` points:
 *
 *   t, lat, lon            n × float64 each
 *   altFt, gsKt, trackDeg  n × float32 each
 *   vsFpm                  n × float32, only when `hasVs`
 *
 * The float64 block is 24n bytes, so every float32 column starts 4-aligned.
 * Written through `DataView` with an explicit byte order rather than by
 * copying `.buffer`, so the file means the same thing on any machine.
 */

export interface PointsHeader {
  trackId: string;
  length: number;
  hasVs: boolean;
  altitudeRef: TrackPoints['altitudeRef'];
}

export function encodePoints(points: TrackPoints): { header: PointsHeader; bytes: Uint8Array } {
  const n = points.t.length;
  const vsFpm = points.vsFpm?.length === n ? points.vsFpm : undefined;
  const hasVs = vsFpm !== undefined;
  const f32Columns = [points.altFt, points.gsKt, points.trackDeg, ...(vsFpm ? [vsFpm] : [])];
  const buffer = new ArrayBuffer(n * 8 * 3 + n * 4 * f32Columns.length);
  const view = new DataView(buffer);
  let offset = 0;
  for (const column of [points.t, points.lat, points.lon]) {
    for (let i = 0; i < n; i += 1) {
      view.setFloat64(offset, column[i] ?? Number.NaN, true);
      offset += 8;
    }
  }
  for (const column of f32Columns) {
    for (let i = 0; i < n; i += 1) {
      view.setFloat32(offset, column[i] ?? Number.NaN, true);
      offset += 4;
    }
  }
  return {
    header: { trackId: points.trackId, length: n, hasVs, altitudeRef: points.altitudeRef },
    bytes: new Uint8Array(buffer),
  };
}

export function decodePoints(header: PointsHeader, bytes: Uint8Array): TrackPoints {
  const n = header.length;
  const expected = n * 24 + n * 4 * (header.hasVs ? 4 : 3);
  if (bytes.byteLength !== expected) {
    throw new Error(
      `Points for ${header.trackId} are ${String(bytes.byteLength)} bytes; expected ${String(expected)}.`,
    );
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 0;
  const f64 = () => {
    const column = new Float64Array(n);
    for (let i = 0; i < n; i += 1) {
      column[i] = view.getFloat64(offset, true);
      offset += 8;
    }
    return column;
  };
  const f32 = () => {
    const column = new Float32Array(n);
    for (let i = 0; i < n; i += 1) {
      column[i] = view.getFloat32(offset, true);
      offset += 4;
    }
    return column;
  };
  const t = f64();
  const lat = f64();
  const lon = f64();
  const altFt = f32();
  const gsKt = f32();
  const trackDeg = f32();
  return {
    trackId: header.trackId,
    t,
    lat,
    lon,
    altFt,
    gsKt,
    trackDeg,
    ...(header.hasVs ? { vsFpm: f32() } : {}),
    altitudeRef: header.altitudeRef,
  };
}
